// ============================================================
// UrbanWear — CartDrawer
// Drawer lateral con lista de ítems del carrito
// ============================================================

import { useCartStore, useUIStore } from '@/store'
import { useAuthStore } from '@/store'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/utils/api'
import { Link } from 'react-router-dom'
import styles from './CartDrawer.module.css'

export default function CartDrawer() {
  const isOpen      = useCartStore(s => s.isOpen)
  const items       = useCartStore(s => s.items)
  const subtotal    = useCartStore(s => s.subtotal())
  const removeItem  = useCartStore(s => s.removeItem)
  const updateQty   = useCartStore(s => s.updateQuantity)
  const toggleCart  = useCartStore(s => s.toggleCart)
	const user          = useAuthStore(s => s.user)
	const { data: siteConfig } = useQuery({
	  queryKey: ['site-config'],
	  queryFn:  () => api.get('/site-config').then(r => r.data),
	})
	const minWholesale    = siteConfig?.wholesale?.minPieces ?? 12
	const totalPieces     = items.reduce((sum, i) => sum + i.quantity, 0)
	const wholesaleActive = !user?.isWholesale && totalPieces >= minWholesale
	const shippingCost     = siteConfig?.store?.shippingCost     ?? 99
	const freeShippingFrom = siteConfig?.store?.freeShippingFrom ?? 1500
	const shipping         = subtotal >= freeShippingFrom ? 0 : shippingCost
  const total    = subtotal + shipping

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className={styles.overlay}
          onClick={toggleCart}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <aside
        className={`${styles.drawer} ${isOpen ? styles.open : ''}`}
        aria-label="Carrito de compras"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>
            Carrito
            {items.length > 0 && (
              <span className={styles.count}>{items.length}</span>
            )}
          </h2>
          <button
            onClick={toggleCart}
            className={styles.closeBtn}
            aria-label="Cerrar carrito"
          >
            <i className="ti ti-x" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className={styles.empty}>
            <i className="ti ti-shopping-bag" />
            <p>Tu carrito está vacío</p>
            <Link to="/colecciones" onClick={toggleCart} className="btn-primary">
              Explorar productos
            </Link>
          </div>
        ) : (
          <>
            <div className={styles.items}>
              {items.map(item => (
                <div key={item.id} className={styles.item}>
                  <div className={styles.itemImg}>
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt={item.productName} />
                      : <i className="ti ti-shirt" />
                    }
                  </div>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemName}>{item.productName}</div>
                    <div className={styles.itemVariant}>{item.variantInfo}</div>
                    <div className={styles.itemBottom}>
                      <div className={styles.qtyControls}>
                        <button
                          onClick={() => updateQty(item.id, item.quantity - 1)}
                          aria-label="Reducir cantidad"
                        >−</button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.availableStock}
                          aria-label="Aumentar cantidad"
                        >+</button>
                      </div>
                      <div className={styles.itemPrice}>
                        ${item.totalPrice.toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className={styles.removeBtn}
                    aria-label="Eliminar del carrito"
                  >
                    <i className="ti ti-trash" />
                  </button>
                </div>
              ))}
            </div>

            <div className={styles.summary}>
			
              <div className={styles.summaryRow}>
                <span>Subtotal</span>
                <span>${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 0 })}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Envío</span>
                <span>{shipping === 0 ? 'Gratis' : `$${shipping}`}</span>
              </div>
              {shipping > 0 && (
                <div className={styles.shippingNote}>
                  Agrega ${(freeShippingFrom - subtotal).toFixed(0)} mas para envio gratis
                </div>
              )}
              <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                <span>Total</span>
                <span>${total.toLocaleString('es-MX', { minimumFractionDigits: 0 })}</span>
              </div>

              <Link
                to="/checkout"
                onClick={toggleCart}
                className={`btn-primary ${styles.checkoutBtn}`}
              >
                Proceder al pago
              </Link>
              <Link
                to="/carrito"
                onClick={toggleCart}
                className={styles.viewCart}
              >
                Ver carrito completo
              </Link>
            </div>
          </>
        )}
      </aside>
    </>
  )
}
